'use client';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Link from 'next/link';
import { Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import {
  ArrowLeft,
  ArrowRight,
  Shield,
  Zap,
  Globe,
  Code2,
  CheckCircle2,
  TrendingUp,
  Network,
  Lock,
  BarChart3,
  Package,
  Cpu,
  Building2,
  Users,
  AlertTriangle,
  Brain,
  Rocket,
  Mail,
} from 'lucide-react';

type Lang = 'en' | 'pl';

function InvestorsContent() {
  const searchParams = useSearchParams();
  const lang: Lang = searchParams.get('lang') === 'pl' ? 'pl' : 'en';
  const isPl = lang === 'pl';

  return (
    <div className="min-h-screen bg-black">
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-rose-900/10 via-black to-black" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f2e_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f2e_1px,transparent_1px)] bg-[size:14px_24px]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-rose-500/5 rounded-full blur-3xl" />
      </div>

      <Header />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="flex items-center justify-between mb-8">
          <Link
            href="/"
            className="inline-flex items-center gap-2 text-neutral-400 hover:text-white transition-colors group"
          >
            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
            <span className="text-sm font-medium">Back</span>
          </Link>
          <div className="inline-flex items-center gap-1 border border-white/10 bg-white/5 p-1">
            <Link
              href="/investors"
              className={`px-3 py-1 text-xs font-semibold transition-colors ${!isPl ? 'bg-white text-black' : 'text-neutral-400 hover:bg-white/10'}`}
            >
              EN
            </Link>
            <Link
              href="/investors?lang=pl"
              className={`px-3 py-1 text-xs font-semibold transition-colors ${isPl ? 'bg-white text-black' : 'text-neutral-400 hover:bg-white/10'}`}
            >
              PL
            </Link>
          </div>
        </div>

        {/* Hero */}
        <div className="mb-16">
          <div className="inline-flex items-center gap-2 text-xs font-medium text-rose-400 px-3 py-1 bg-rose-500/10 border border-rose-500/20 mb-6">
            {isPl ? 'Runda Pre-seed · Czerwiec 2026' : 'Pre-seed Round · June 2026'}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 leading-tight">
            <span className="bg-gradient-to-r from-white to-neutral-400 bg-clip-text text-transparent">
              {isPl
                ? 'Ochrona runtime dla aplikacji pisanych przez AI'
                : 'Runtime security for apps written by AI'}
            </span>
          </h1>
          <p className="text-base text-neutral-400 max-w-2xl leading-relaxed">
            {isPl
              ? 'AI generuje dziś więcej kodu niż ludzie są w stanie sprawdzić - i ten kod trafia na produkcję bez żadnej ochrony w czasie działania. Silker AI to warstwa bezpieczeństwa instalowana jedną linią, która blokuje atak zanim dotrze on do logiki aplikacji. Decyzja zapada lokalnie, w mniej niż milisekundę.'
              : 'AI now writes more code than humans can review, and that code ships to production with zero runtime protection. Silker AI is a one-line security layer that blocks an attack before it ever reaches your application logic. The decision is made locally, in under a millisecond.'}
          </p>
        </div>

        {/* Traction metrics */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-16">
          {[
            { value: '0%', label: isPl ? 'false positives, trasy LLM' : 'false positives, LLM routes', sub: isPl ? 'z 24% - tuning detekcji' : 'down from 24% - tuned' },
            { value: '96.0%', label: isPl ? 'skuteczność detekcji' : 'threat detection rate', sub: isPl ? 'true-positive rate' : 'true-positive rate' },
            { value: '~0ms', label: isPl ? 'narzut p99' : 'p99 overhead', sub: isPl ? 'lokalnie, bez proxy' : 'local, no proxy' },
            { value: '3', label: isPl ? 'warstwy dostarczania' : 'delivery layers', sub: 'SDK · Worker · Docker' },
          ].map((s) => (
            <div key={s.label} className="p-4 border border-white/5 bg-neutral-900/30">
              <div className="text-2xl font-bold text-white font-mono mb-0.5">{s.value}</div>
              <div className="text-xs text-neutral-400 mb-0.5">{s.label}</div>
              <div className="text-[10px] text-neutral-600 font-mono">{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Problem */}
        <section className="mb-16">
          <div className="inline-flex items-center gap-2 text-xs text-neutral-500 mb-4">
            <div className="w-6 h-[1px] bg-gradient-to-r from-transparent to-neutral-700" />
            {isPl ? 'Problem' : 'The Problem'}
            <div className="w-6 h-[1px] bg-gradient-to-l from-transparent to-neutral-700" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            {isPl ? 'Ochrona kończy się na brzegu sieci' : 'Protection stops at the network edge'}
          </h2>
          <p className="text-sm text-neutral-400 leading-relaxed mb-6 max-w-2xl">
            {isPl
              ? 'Dzisiejsze aplikacje - zwłaszcza te pisane przez AI - to API, agenci i mikrousługi. WAF widzi tylko surowy ruch HTTP: nie zna logiki aplikacji, nie wie kto jest zalogowany ani co naprawdę wykonuje kod. Najgroźniejsze ataki dzieją się dokładnie tam, gdzie WAF nie sięga - w runtime.'
              : "Today's apps - especially AI-written ones - are APIs, agents, and microservices. A WAF only sees raw HTTP: it has no idea what the app logic is, who is authenticated, or what the code actually executes. The most damaging attacks happen exactly where a WAF cannot reach - at runtime."}
          </p>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              {
                icon: AlertTriangle,
                color: 'text-red-400',
                bg: 'bg-red-500/5',
                border: 'border-red-500/15',
                title: isPl ? 'Martwe punkty' : 'Blind spots',
                body: isPl
                  ? 'Narzędzia sieciowe nie wykryją obejścia autoryzacji (IDOR), nadużyć logiki biznesowej ani prompt injection u agentów AI. To dziś najczęstsze i najdroższe wektory włamań.'
                  : 'Network tools miss authorization bypass (IDOR), business-logic abuse, and prompt injection in AI agents - today the most common and most expensive breach vectors.',
              },
              {
                icon: Zap,
                color: 'text-orange-400',
                bg: 'bg-orange-500/5',
                border: 'border-orange-500/15',
                title: isPl ? 'Szum zamiast sygnału' : 'Noise, not signal',
                body: isPl
                  ? 'Generyczne sygnatury bez kontekstu aplikacji zalewają zespół fałszywymi alarmami. Efekt: blokowani realni użytkownicy i alerty, które nikt już nie czyta.'
                  : 'Generic signatures with no app context flood teams with false positives. The result: real users get blocked and alerts nobody reads anymore.',
              },
              {
                icon: Code2,
                color: 'text-yellow-400',
                bg: 'bg-yellow-500/5',
                border: 'border-yellow-500/15',
                title: isPl ? 'Wolna naprawa' : 'Slow remediation',
                body: isPl
                  ? 'Alert bez stack trace i wartości zmiennych to zgadywanka. Deweloperzy tracą godziny na odtworzenie problemu, zamiast go po prostu naprawić.'
                  : 'An alert with no stack trace and no variable values is guesswork. Developers burn hours reproducing the issue instead of just fixing it.',
              },
            ].map(({ icon: Icon, color, bg, border, title, body }) => (
              <div key={title} className={`p-5 rounded-none border ${border} ${bg}`}>
                <Icon className={`w-5 h-5 ${color} mb-3`} />
                <h3 className="text-sm font-semibold text-white mb-2">{title}</h3>
                <p className="text-xs text-neutral-500 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Solution */}
        <section className="mb-16">
          <div className="inline-flex items-center gap-2 text-xs text-neutral-500 mb-4">
            <div className="w-6 h-[1px] bg-gradient-to-r from-transparent to-neutral-700" />
            {isPl ? 'Rozwiązanie' : 'The Solution'}
            <div className="w-6 h-[1px] bg-gradient-to-l from-transparent to-neutral-700" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            {isPl ? 'Jeden silnik. Trzy warstwy dostarczania.' : 'One engine. Three delivery layers.'}
          </h2>
          <p className="text-sm text-neutral-400 leading-relaxed mb-6 max-w-2xl">
            {isPl
              ? '@silker-ai/core to jeden silnik detekcji działający na edge (V8, zero zależności od Node). Ten sam kod chroni aplikacje Next.js przez SDK, dowolną domenę przez Cloudflare Worker i każdy backend przez kontener Docker. Decyzja blokuj/przepuść zapada lokalnie, bez proxy i bez round-tripu - do nas trafia wyłącznie telemetria, asynchronicznie. Twoje dane nigdy nie opuszczają Twojej infrastruktury.'
              : '@silker-ai/core is a single detection engine that runs on the edge (V8, zero Node.js deps). The same code protects Next.js apps via SDK, any domain via Cloudflare Worker, and any backend via Docker container. The block/allow decision is local - no proxy, no round-trip - and only telemetry reaches us, asynchronously. Your data never leaves your own infrastructure.'}
          </p>
          <div className="grid sm:grid-cols-3 gap-3 mb-6">
            {[
              {
                icon: Package,
                title: 'npm i @silker-ai/agent',
                label: 'Node.js / Next.js SDK',
                body: isPl ? 'Jeden import w instrumentation.ts albo middleware Express. Działa w mniej niż minutę, bez zmian w architekturze.' : 'One import in instrumentation.ts or Express middleware. Live in under a minute, no architecture changes.',
              },
              {
                icon: Globe,
                title: 'wrangler deploy',
                label: 'Cloudflare Worker',
                body: isPl ? 'Zero zmian w kodzie aplikacji. Blokuje zagrożenia na edge, zanim dotrą do originu.' : 'Zero app-code changes. Blocks threats at the edge, before they ever reach your origin.',
              },
              {
                icon: Cpu,
                title: 'docker compose up',
                label: isPl ? 'Kontener self-host' : 'Self-hosted container',
                body: isPl ? 'Reverse proxy dla dowolnego backendu - PHP, Python, Java, Go. Działa na każdym VPS, dane zostają u Ciebie.' : 'Reverse proxy for any backend - PHP, Python, Java, Go. Runs on any VPS, your data stays with you.',
              },
            ].map(({ icon: Icon, title, label, body }) => (
              <div key={title} className="p-5 rounded-none border border-emerald-500/15 bg-emerald-500/5">
                <Icon className="w-5 h-5 text-emerald-400 mb-3" />
                <code className="text-[10px] font-mono text-emerald-500/70 bg-emerald-500/5 border border-emerald-500/15 px-2 py-0.5 block mb-2">{title}</code>
                <h3 className="text-sm font-semibold text-white mb-1">{label}</h3>
                <p className="text-xs text-neutral-500 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
          <div className="p-5 border border-white/5 bg-neutral-900/20">
            <div className="flex flex-wrap gap-4 text-xs text-neutral-500">
              {['SQLi', 'XSS', 'Path Traversal', 'Prompt Injection', 'PII / Secret Leak', isPl ? 'Nadużycia rate limit' : 'Rate Limit Abuse', isPl ? 'Upload plików' : 'File Upload Abuse'].map((tag) => (
                <span key={tag} className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Why now */}
        <section className="mb-16">
          <div className="inline-flex items-center gap-2 text-xs text-neutral-500 mb-4">
            <div className="w-6 h-[1px] bg-gradient-to-r from-transparent to-neutral-700" />
            {isPl ? 'Dlaczego teraz' : 'Why Now'}
            <div className="w-6 h-[1px] bg-gradient-to-l from-transparent to-neutral-700" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            {isPl ? 'Powierzchnia ataku rośnie szybciej niż bezpieczeństwo' : 'Attack surface is growing faster than security'}
          </h2>
          <p className="text-sm text-neutral-400 leading-relaxed mb-6 max-w-2xl">
            {isPl
              ? 'AI pisze kod szybciej, niż ludzie zdążą go sprawdzić. Agenci AI wykonują sekwencje wywołań narzędzi, których żaden WAF nie rozumie. Vibe-coding stał się normą - setki tysięcy aplikacji trafiają na produkcję bez jednej linii security review. Powierzchnia ataku eksploduje, a ochrona nie nadąża.'
              : 'AI writes code faster than humans can review it. AI agents run tool-call sequences no WAF understands. Vibe-coding is now the norm - hundreds of thousands of apps ship without a single line of security review. The attack surface is exploding while protection lags behind.'}
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              {
                icon: Brain,
                title: isPl ? 'Kod od AI trafia na produkcję bez review' : 'AI code ships to production without review',
                body: isPl ? 'v0, Lovable, Bolt, Cursor - narzędzia, z których codziennie korzystają miliony. Każda wygenerowana aplikacja to potencjalny wektor ataku bez żadnej ochrony w runtime.' : 'v0, Lovable, Bolt, Cursor - tools millions use every day. Every generated app is a potential attack vector with no runtime protection at all.',
              },
              {
                icon: Network,
                title: isPl ? 'Agentowe AI tworzy zupełnie nową powierzchnię ataku' : 'Agentic AI creates an entirely new attack surface',
                body: isPl ? 'Prompt injection, manipulacja wywołań narzędzi, zatruwanie pamięci - klasy ataków, których WAF w ogóle nie widzi. Runtime to jedyna warstwa, która je wychwytuje.' : 'Prompt injection, tool-call manipulation, memory poisoning - attack classes a WAF cannot see at all. Runtime is the only layer that catches them.',
              },
              {
                icon: TrendingUp,
                title: isPl ? 'Regulacje AI już nadchodzą' : 'AI regulation is already coming',
                body: isPl ? 'EU AI Act, NIST AI RMF, OWASP LLM Top 10 - compliance wymaga udokumentowanej ochrony runtime. Silker generuje raporty gotowe pod audyt automatycznie.' : 'EU AI Act, NIST AI RMF, OWASP LLM Top 10 - compliance demands documented runtime protection. Silker generates audit-ready reports automatically.',
              },
              {
                icon: Lock,
                title: isPl ? 'Wygrywa security tworzone dla deweloperów' : 'Developer-first security has won',
                body: isPl ? 'Snyk, Datadog, Vercel - dystrybucja przez npm/CLI/SDK to jedyna realna droga do adopcji. Silker jest SDK-first od pierwszego dnia.' : 'Snyk, Datadog, Vercel - distribution via npm/CLI/SDK is the only real path to adoption. Silker has been SDK-first from day one.',
              },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="p-5 rounded-none border border-blue-500/10 bg-blue-500/5">
                <Icon className="w-5 h-5 text-blue-400 mb-3" />
                <h3 className="text-sm font-semibold text-white mb-1">{title}</h3>
                <p className="text-xs text-neutral-500 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Business model & GTM */}
        <section className="mb-16">
          <div className="inline-flex items-center gap-2 text-xs text-neutral-500 mb-4">
            <div className="w-6 h-[1px] bg-gradient-to-r from-transparent to-neutral-700" />
            {isPl ? 'Model biznesowy' : 'Business Model'}
            <div className="w-6 h-[1px] bg-gradient-to-l from-transparent to-neutral-700" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-6">
            {isPl ? 'Freemium → SaaS → Enterprise' : 'Freemium → SaaS → Enterprise'}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            {[
              {
                tier: 'Starter',
                price: isPl ? 'Bezpłatny' : 'Free',
                color: 'border-white/5',
                highlight: false,
                items: [
                  isPl ? '1 aplikacja' : '1 application',
                  isPl ? '10k żądań/mies.' : '10k requests/mo',
                  isPl ? 'Blokowanie AI + OWASP' : 'AI + OWASP blocking',
                  isPl ? 'Samodzielna instalacja' : 'Self-service install',
                ],
              },
              {
                tier: 'Pro',
                price: '$39/mo',
                color: 'border-rose-500/30',
                highlight: true,
                items: [
                  isPl ? '2 aplikacje · 500k żądań/mies.' : '2 apps · 500k requests/mo',
                  isPl ? 'AI Verdict + Copilot' : 'AI Verdict + Copilot',
                  isPl ? 'Pentest + Threat Map' : 'Pentest + Threat Map',
                  isPl ? 'Retencja 30 dni' : '30-day retention',
                ],
              },
              {
                tier: 'Business',
                price: '$99/mo',
                color: 'border-orange-500/25',
                highlight: false,
                items: [
                  isPl ? '5 aplikacji · żądania bez limitu' : '5 apps · unlimited requests',
                  isPl ? 'Wszystko z Pro' : 'Everything in Pro',
                  isPl ? 'Zespół + SSO Google' : 'Team seats + Google SSO',
                  isPl ? 'Alerty Slack + priorytet' : 'Slack alerts + priority',
                ],
              },
              {
                tier: 'Enterprise',
                price: isPl ? 'Na zapytanie' : 'Custom',
                color: 'border-violet-500/20',
                highlight: false,
                items: [
                  isPl ? 'Self-host / on-prem' : 'Self-host / on-prem',
                  isPl ? 'Aplikacje i żądania bez limitu' : 'Unlimited apps & requests',
                  isPl ? 'SAML SSO + eksport SIEM' : 'SAML SSO + SIEM export',
                  isPl ? 'SLA + dedykowany CS' : 'SLA + dedicated CS',
                ],
              },
            ].map(({ tier, price, color, highlight, items }) => (
              <div key={tier} className={`p-5 rounded-none border ${color} ${highlight ? 'bg-rose-500/5' : 'bg-neutral-900/30'}`}>
                <div className="flex items-baseline justify-between mb-3">
                  <span className="text-sm font-bold text-white">{tier}</span>
                  <span className={`text-sm font-mono ${highlight ? 'text-rose-400' : 'text-neutral-400'}`}>{price}</span>
                </div>
                <ul className="space-y-1.5">
                  {items.map((item) => (
                    <li key={item} className="flex items-center gap-2 text-xs text-neutral-500">
                      <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="text-xs text-neutral-500 mb-6">
            {isPl
              ? 'Oferta beta: -50% przez pierwsze 3 miesiące. Płatności live przez Stripe (subskrypcje, customer portal, webhooki), rozliczane miesięcznie lub rocznie.'
              : 'Beta offer: 50% off for the first 3 months. Payments are live via Stripe (subscriptions, customer portal, webhooks), billed monthly or annually.'}
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="p-5 border border-white/5 bg-neutral-900/20">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-rose-400" />
                <span className="text-sm font-semibold text-white">ICP</span>
              </div>
              <ul className="space-y-1 text-xs text-neutral-500">
                <li>{isPl ? 'Vibe-coderzy i indie deweloperzy (v0, Lovable, Bolt, Cursor)' : 'Vibe-coders and indie devs (v0, Lovable, Bolt, Cursor)'}</li>
                <li>{isPl ? 'Małe zespoły budujące aplikacje AI na Next.js / Vercel' : 'Small teams building AI apps on Next.js / Vercel'}</li>
                <li>{isPl ? 'B2B SaaS z API o dużym ruchu i bez własnego zespołu security' : 'B2B SaaS with high-traffic APIs and no dedicated security team'}</li>
              </ul>
            </div>
            <div className="p-5 border border-white/5 bg-neutral-900/20">
              <div className="flex items-center gap-2 mb-2">
                <Rocket className="w-4 h-4 text-rose-400" />
                <span className="text-sm font-semibold text-white">GTM</span>
              </div>
              <ul className="space-y-1 text-xs text-neutral-500">
                <li>{isPl ? 'Bottom-up: darmowy SDK na npm → dashboard → konwersja na płatny plan' : 'Bottom-up: free npm SDK → dashboard → conversion to a paid plan'}</li>
                <li>{isPl ? 'Launch na Product Hunt (gotowy) + społeczności deweloperskie' : 'Product Hunt launch (ready) + developer communities'}</li>
                <li>{isPl ? 'Up-sell do Enterprise: compliance, governance, on-prem' : 'Up-sell into Enterprise: compliance, governance, on-prem'}</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Competition */}
        <section className="mb-16">
          <div className="inline-flex items-center gap-2 text-xs text-neutral-500 mb-4">
            <div className="w-6 h-[1px] bg-gradient-to-r from-transparent to-neutral-700" />
            {isPl ? 'Konkurencja' : 'Competition'}
            <div className="w-6 h-[1px] bg-gradient-to-l from-transparent to-neutral-700" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-6">
            {isPl ? 'Inna warstwa, inny wynik' : 'Different layer, different outcome'}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 pr-4 text-neutral-500 font-medium"></th>
                  <th className="text-center py-3 px-3 text-neutral-500 font-medium">WAF</th>
                  <th className="text-center py-3 px-3 text-neutral-500 font-medium">SAST/SCA</th>
                  <th className="text-center py-3 px-3 text-neutral-500 font-medium">Observability</th>
                  <th className="text-center py-3 px-3 text-rose-400 font-semibold">Silker AI</th>
                </tr>
              </thead>
              <tbody className="text-neutral-500">
                {[
                  { feature: isPl ? 'Blokuje w runtime' : 'Blocks at runtime', waf: '✓', sast: '✗', obs: '✗', silker: '✓' },
                  { feature: isPl ? 'Kontekst kodu / logiki' : 'App logic context', waf: '✗', sast: 'partial', obs: '✗', silker: '✓' },
                  { feature: isPl ? 'Prompt injection AI' : 'AI prompt injection', waf: '✗', sast: '✗', obs: '✗', silker: '✓' },
                  { feature: isPl ? 'Instalacja w 1 linii kodu' : '1-line install', waf: '✗', sast: '✓', obs: '✓', silker: '✓' },
                  { feature: isPl ? 'Zero opóźnienia' : 'Zero added latency', waf: 'partial', sast: '-', obs: '-', silker: '✓' },
                  { feature: isPl ? 'Działa na edge (CF)' : 'Runs on edge (CF)', waf: 'partial', sast: '✗', obs: '✗', silker: '✓' },
                ].map(({ feature, waf, sast, obs, silker }) => (
                  <tr key={feature} className="border-b border-white/5">
                    <td className="py-2.5 pr-4 text-neutral-400">{feature}</td>
                    <td className="text-center py-2.5 px-3">{waf === '✓' ? <span className="text-emerald-500">✓</span> : waf === '✗' ? <span className="text-red-500/60">✗</span> : <span className="text-neutral-600">{waf}</span>}</td>
                    <td className="text-center py-2.5 px-3">{sast === '✓' ? <span className="text-emerald-500">✓</span> : sast === '✗' ? <span className="text-red-500/60">✗</span> : <span className="text-neutral-600">{sast}</span>}</td>
                    <td className="text-center py-2.5 px-3">{obs === '✓' ? <span className="text-emerald-500">✓</span> : obs === '✗' ? <span className="text-red-500/60">✗</span> : <span className="text-neutral-600">{obs}</span>}</td>
                    <td className="text-center py-2.5 px-3 font-semibold"><span className="text-emerald-400">✓</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Roadmap summary */}
        <section className="mb-16">
          <div className="inline-flex items-center gap-2 text-xs text-neutral-500 mb-4">
            <div className="w-6 h-[1px] bg-gradient-to-r from-transparent to-neutral-700" />
            {isPl ? 'Roadmapa' : 'Roadmap'}
            <div className="w-6 h-[1px] bg-gradient-to-l from-transparent to-neutral-700" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-6">
            {isPl ? 'Gdzie jesteśmy i dokąd zmierzamy' : "Where we are and where we're going"}
          </h2>
          <div className="grid sm:grid-cols-3 gap-3 mb-4">
            {[
              {
                period: 'Q3 2025 – Q1 2026',
                status: isPl ? 'Wysłane' : 'Shipped',
                color: 'border-emerald-500/20 bg-emerald-500/5',
                dot: 'bg-emerald-400',
                items: isPl
                  ? ['SDK + Worker + Docker', 'Dashboard + telemetria', 'Billing Stripe + paywalle', 'AI Verdict + zespół/SSO']
                  : ['SDK + Worker + Docker', 'Dashboard + telemetry', 'Stripe billing + paywalls', 'AI Verdict + team/SSO'],
              },
              {
                period: 'Q2 2026',
                status: isPl ? 'W trakcie' : 'In progress',
                color: 'border-rose-500/20 bg-rose-500/5',
                dot: 'bg-rose-400 animate-pulse',
                items: isPl
                  ? ['Detekcja semantyczna (embeddings)', 'AI Copilot - wyjaśnij i napraw', 'Inspekcja odpowiedzi', 'Guardraile dla streamu LLM']
                  : ['Semantic detection (embeddings)', 'AI Copilot - explain & fix', 'Response inspection', 'Streaming LLM guardrails'],
              },
              {
                period: 'Q3–Q4 2026',
                status: isPl ? 'Zaplanowane' : 'Planned',
                color: 'border-blue-500/10 bg-blue-500/5',
                dot: 'bg-blue-400',
                items: isPl
                  ? ['Config sync', 'SIEM / webhooks', 'CI/CD security gate', 'Enterprise self-host']
                  : ['Config sync', 'SIEM / webhooks', 'CI/CD security gate', 'Enterprise self-host'],
              },
            ].map(({ period, status, color, dot, items }) => (
              <div key={period} className={`p-5 rounded-none border ${color}`}>
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-2 h-2 rounded-full ${dot}`} />
                  <span className="text-[10px] font-mono text-neutral-500 uppercase tracking-widest">{status}</span>
                </div>
                <div className="text-xs text-neutral-500 font-mono mb-3">{period}</div>
                <ul className="space-y-1.5">
                  {items.map((item) => (
                    <li key={item} className="text-xs text-neutral-400 flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full bg-neutral-600 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <Link
            href="/roadmap"
            className="inline-flex items-center gap-2 text-sm text-rose-400 hover:text-rose-300 transition-colors group"
          >
            {isPl ? 'Pełna roadmapa techniczna' : 'View full technical roadmap'}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </section>

        {/* The Ask */}
        <section className="mb-16">
          <div className="rounded-none border border-amber-500/20 bg-amber-500/5 p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-10 h-10 rounded-none bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
                <BarChart3 className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white mb-1">
                  {isPl ? 'Inwestycja' : 'The Ask'}
                </h2>
                <p className="text-sm text-neutral-400 leading-relaxed max-w-2xl">
                  {isPl
                    ? 'Prowadzimy rundę pre-seed. Produkt jest live: SDK na npm, dashboard i pełne płatności Stripe działają już dziś - infrastruktura monetyzacji jest gotowa, nie planowana. Szukamy inwestorów, którzy rozumieją developer tooling, AI security i dystrybucję przez SDK. Kapitał przeznaczymy na trzy rzeczy: warstwę detekcji AI wraz z domknięciem pętli danych (werdykty FP/TP → auto-tuning per aplikacja - przewaga, której nie da się skopiować), zatrudnienie pierwszego inżyniera oraz kampanię GTM (Product Hunt + społeczności deweloperskie). Cel rundy: pierwsze płatne konta i powtarzalny przychód.'
                    : 'We are running a pre-seed round. The product is live: the SDK is on npm, the dashboard ships, and full Stripe billing already works today - monetization infrastructure is done, not planned. We are looking for investors who understand developer tooling, AI security, and SDK-led distribution. Capital goes to three things: the AI detection layer plus closing the data loop (FP/TP verdicts → per-app auto-tuning - a moat competitors cannot copy), the first engineering hire, and the GTM campaign (Product Hunt + developer communities). The goal of this round: first paying accounts and repeatable revenue.'}
                </p>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3 mb-6">
              {[
                {
                  icon: Package,
                title: isPl ? 'Produkt' : 'Product',
                body: isPl ? 'Detekcja semantyczna, AI Copilot, inspekcja odpowiedzi oraz pętla danych FP/TP, która z każdym klientem wzmacnia silnik detekcji.' : 'Semantic detection, AI Copilot, response inspection, and the FP/TP data loop that hardens the detection engine with every customer.',
                },
                {
                  icon: Rocket,
                title: isPl ? 'Go-to-market' : 'Go-to-market',
                body: isPl ? 'Launch na Product Hunt, społeczności deweloperskie, design partnerzy. Cel: pierwsze płatne konta.' : 'Product Hunt launch, developer communities, design partners. Target: first paying accounts.',
                },
                {
                  icon: Building2,
                title: isPl ? 'Zespół' : 'Team',
                body: isPl ? 'Pierwszy inżynier na pokładzie oraz fundamenty compliance i bezpieczeństwa pod enterprise.' : 'First engineering hire, plus compliance and security foundations for enterprise.',
                },
              ].map(({ icon: Icon, title, body }) => (
                <div key={title} className="p-4 border border-amber-500/10 bg-amber-500/5">
                  <Icon className="w-4 h-4 text-amber-400 mb-2" />
                  <h3 className="text-sm font-semibold text-white mb-1">{title}</h3>
                  <p className="text-xs text-neutral-500 leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <a
                href="mailto:investors@silkerai.com"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-black rounded-none text-sm font-medium hover:bg-neutral-100 transition-colors"
              >
                <Mail className="w-4 h-4" />
                investors@silkerai.com
              </a>
              <Link
                href="/contact"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-none border border-amber-500/30 text-amber-400 text-sm font-medium hover:bg-amber-500/10 transition-colors"
              >
                {isPl ? 'Formularz kontaktowy' : 'Contact form'}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Footer CTA */}
        <div className="rounded-none border border-neutral-800/50 bg-neutral-950/50 p-6 text-center">
          <p className="text-sm text-neutral-400 mb-4">
            {isPl ? 'Chcesz zobaczyć produkt w akcji?' : 'Want to see the product in action?'}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              href="/docs/quickstart"
              className="inline-flex items-center gap-2 px-5 py-2.5 border border-neutral-700 text-white text-sm font-medium hover:border-rose-500/40 hover:bg-rose-500/5 transition-colors"
            >
              {isPl ? 'Quickstart (5 min)' : 'Quickstart (5 min)'}
            </Link>
            <Link
              href="/benchmarks"
              className="inline-flex items-center gap-2 px-5 py-2.5 border border-neutral-700 text-white text-sm font-medium hover:border-rose-500/40 hover:bg-rose-500/5 transition-colors"
            >
              {isPl ? 'Benchmarki detekcji' : 'Detection benchmarks'}
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default function InvestorsPage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-black" />}>
      <InvestorsContent />
    </Suspense>
  );
}
